import { storage } from "@/firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { logDebug, logError, logWarn, logSuccess } from "./secureLogger";

// Allowed file types for session materials
export const ALLOWED_FILE_TYPES = {
  // Documents
  "application/pdf": ".pdf",
  "application/msword": ".doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",

  // Presentations
  "application/vnd.ms-powerpoint": ".ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx",

  // Text files
  "text/plain": ".txt",

  // Images
  "image/jpeg": ".jpg, .jpeg",
  "image/png": ".png",
  "image/gif": ".gif",
  "image/webp": ".webp",
};

// Maximum file size (10MB)
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

// Maximum number of files
export const MAX_FILES = 5;

export interface UploadedFile {
  name: string;
  url: string;
  type: string;
  size: number;
  path: string;
  uploadedAt: Date;
}

/**
 * ✅ SECURITY: Verify file signature (magic bytes) matches file extension
 * Prevents malware disguised as PDF/DOCX/TXT/Images (e.g., renamed .exe files)
 */
async function verifyFileSignature(
  file: File
): Promise<{ isValid: boolean; detectedType: string; reason?: string }> {
  try {
    // Read first 8 bytes (enough to identify most file types)
    const blob = file.slice(0, 8);
    const arrayBuffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);

    const hexSignature = Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join(" ")
      .toUpperCase();

    logDebug(`🔒 File signature check for ${file.name}`, {
      signature: hexSignature,
      fileName: file.name,
    });

    const fileName = file.name.toLowerCase();

    // ✅ PDF: Must start with "%PDF-" (25 50 44 46 2D)
    if (fileName.endsWith(".pdf")) {
      if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
        return { isValid: true, detectedType: "pdf" };
      }
      return {
        isValid: false,
        detectedType: "unknown",
        reason: "Invalid PDF signature. File may be corrupted or disguised.",
      };
    }

    // ✅ DOCX/PPTX: Must start with PK (50 4B) - ZIP archive signature
    if (fileName.endsWith(".docx") || fileName.endsWith(".pptx")) {
      if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
        return { isValid: true, detectedType: fileName.endsWith(".docx") ? "docx" : "pptx" };
      }
      return {
        isValid: false,
        detectedType: "unknown",
        reason: "Invalid Office document signature. File may be corrupted or disguised.",
      };
    }

    // ✅ DOC/PPT (Legacy): Must start with D0 CF 11 E0 (Microsoft OLE signature)
    if (fileName.endsWith(".doc") || fileName.endsWith(".ppt")) {
      if (bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0) {
        return { isValid: true, detectedType: fileName.endsWith(".doc") ? "doc" : "ppt" };
      }
      return {
        isValid: false,
        detectedType: "unknown",
        reason: "Invalid legacy Office document signature. File may be corrupted or disguised.",
      };
    }

    // ✅ JPEG: Must start with FF D8 FF
    if (fileName.endsWith(".jpg") || fileName.endsWith(".jpeg")) {
      if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
        return { isValid: true, detectedType: "jpeg" };
      }
      return {
        isValid: false,
        detectedType: "unknown",
        reason: "Invalid JPEG signature. File may be corrupted or disguised.",
      };
    }

    // ✅ PNG: Must start with 89 50 4E 47
    if (fileName.endsWith(".png")) {
      if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
        return { isValid: true, detectedType: "png" };
      }
      return {
        isValid: false,
        detectedType: "unknown",
        reason: "Invalid PNG signature. File may be corrupted or disguised.",
      };
    }

    // ✅ GIF: Must start with "GIF" (47 49 46)
    if (fileName.endsWith(".gif")) {
      if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
        return { isValid: true, detectedType: "gif" };
      }
      return {
        isValid: false,
        detectedType: "unknown",
        reason: "Invalid GIF signature. File may be corrupted or disguised.",
      };
    }

    // ✅ WEBP: Must contain "WEBP" signature (52 49 46 46 ... 57 45 42 50)
    if (fileName.endsWith(".webp")) {
      if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) {
        return { isValid: true, detectedType: "webp" };
      }
      return {
        isValid: false,
        detectedType: "unknown",
        reason: "Invalid WEBP signature. File may be corrupted or disguised.",
      };
    }

    // ✅ TXT: Plain text (no specific signature, validate it's not executable)
    if (fileName.endsWith(".txt")) {
      // Check if it's NOT an executable (MZ header = 4D 5A)
      if (bytes[0] === 0x4d && bytes[1] === 0x5a) {
        return {
          isValid: false,
          detectedType: "executable",
          reason: "File is an executable disguised as TXT. This is dangerous.",
        };
      }
      return { isValid: true, detectedType: "txt" };
    }

    return { isValid: false, detectedType: "unknown", reason: "Unsupported file type" };
  } catch (error) {
    logError(`Failed to verify file signature for ${file.name}`, error);
    return {
      isValid: false,
      detectedType: "error",
      reason: "Could not read file signature",
    };
  }
}

/**
 * ✅ SECURITY: Verify MIME type matches expected file type
 */
function verifyMIMEType(file: File): { isValid: boolean; reason?: string } {
  const fileName = file.name.toLowerCase();
  const mimeType = file.type.toLowerCase();

  logDebug(`🔒 MIME type check for ${file.name}`, {
    reportedMIME: mimeType || "(empty)",
    fileName: file.name,
  });

  // PDF
  if (fileName.endsWith(".pdf")) {
    if (mimeType !== "application/pdf" && mimeType !== "") {
      return {
        isValid: false,
        reason: `PDF file has invalid MIME type: ${mimeType}. Expected: application/pdf`,
      };
    }
    return { isValid: true };
  }

  // DOCX
  if (fileName.endsWith(".docx")) {
    const validDocxMimes = [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "", // Browser might not set MIME for some files
    ];
    if (!validDocxMimes.includes(mimeType)) {
      return {
        isValid: false,
        reason: `DOCX file has invalid MIME type: ${mimeType}`,
      };
    }
    return { isValid: true };
  }

  // DOC (Legacy)
  if (fileName.endsWith(".doc")) {
    const validDocMimes = ["application/msword", ""];
    if (!validDocMimes.includes(mimeType)) {
      return {
        isValid: false,
        reason: `DOC file has invalid MIME type: ${mimeType}`,
      };
    }
    return { isValid: true };
  }

  // PPTX
  if (fileName.endsWith(".pptx")) {
    const validPptxMimes = [
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "",
    ];
    if (!validPptxMimes.includes(mimeType)) {
      return {
        isValid: false,
        reason: `PPTX file has invalid MIME type: ${mimeType}`,
      };
    }
    return { isValid: true };
  }

  // PPT (Legacy)
  if (fileName.endsWith(".ppt")) {
    const validPptMimes = ["application/vnd.ms-powerpoint", ""];
    if (!validPptMimes.includes(mimeType)) {
      return {
        isValid: false,
        reason: `PPT file has invalid MIME type: ${mimeType}`,
      };
    }
    return { isValid: true };
  }

  // TXT
  if (fileName.endsWith(".txt")) {
    const dangerousMimes = [
      "application/x-msdownload",
      "application/x-msdos-program",
      "application/octet-stream",
    ];
    if (dangerousMimes.includes(mimeType)) {
      return {
        isValid: false,
        reason: `TXT file has suspicious MIME type: ${mimeType}. This might be an executable.`,
      };
    }
    return { isValid: true };
  }

  // Images
  if (fileName.endsWith(".jpg") || fileName.endsWith(".jpeg")) {
    if (!mimeType.startsWith("image/") && mimeType !== "") {
      return {
        isValid: false,
        reason: `JPEG file has invalid MIME type: ${mimeType}`,
      };
    }
    return { isValid: true };
  }

  if (fileName.endsWith(".png")) {
    if (mimeType !== "image/png" && mimeType !== "") {
      return {
        isValid: false,
        reason: `PNG file has invalid MIME type: ${mimeType}`,
      };
    }
    return { isValid: true };
  }

  if (fileName.endsWith(".gif")) {
    if (mimeType !== "image/gif" && mimeType !== "") {
      return {
        isValid: false,
        reason: `GIF file has invalid MIME type: ${mimeType}`,
      };
    }
    return { isValid: true };
  }

  if (fileName.endsWith(".webp")) {
    if (mimeType !== "image/webp" && mimeType !== "") {
      return {
        isValid: false,
        reason: `WEBP file has invalid MIME type: ${mimeType}`,
      };
    }
    return { isValid: true };
  }

  return { isValid: true };
}

/**
 * ✅ ENHANCED: Validates a file with comprehensive security checks
 */
export async function validateFile(file: File): Promise<{ valid: boolean; error?: string }> {
  const validationId = `${file.name}_${Date.now()}`;
  logDebug(`🔒 Starting file validation for ${file.name}`, { validationId });

  // Step 1: Check file extension
  const fileName = file.name.toLowerCase();
  const allowedExtensions = [
    ".pdf",
    ".doc",
    ".docx",
    ".ppt",
    ".pptx",
    ".txt",
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
  ];
  const hasValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext));

  if (!hasValidExtension) {
    logWarn(`❌ File rejected: Invalid extension`, { fileName, validationId });
    return {
      valid: false,
      error: `Invalid file type. Allowed: PDF, Word, PowerPoint, TXT, Images (JPG, PNG, GIF, WEBP)`,
    };
  }

  // Step 2: Check file size
  if (file.size > MAX_FILE_SIZE) {
    logWarn(`❌ File rejected: Size exceeds limit`, { fileName, size: file.size, validationId });
    return {
      valid: false,
      error: `File size exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit`,
    };
  }

  // Step 3: Check if file is empty
  if (file.size < 10) {
    logWarn(`❌ File rejected: File is empty or corrupted`, {
      fileName,
      size: file.size,
      validationId,
    });
    return {
      valid: false,
      error: "File is empty or corrupted. Please select a valid file.",
    };
  }

  // Step 4: Verify MIME type
  logDebug(`🔒 Step 1: MIME type validation`, { validationId });
  const mimeCheck = verifyMIMEType(file);
  if (!mimeCheck.isValid) {
    logWarn(`❌ File rejected: MIME type validation failed`, {
      fileName,
      reason: mimeCheck.reason,
      validationId,
    });
    return {
      valid: false,
      error: mimeCheck.reason || "Invalid file type detected",
    };
  }
  logSuccess(`✅ MIME type validation passed`, { validationId });

  // Step 5: Verify file signature (magic bytes)
  logDebug(`🔒 Step 2: File signature validation`, { validationId });
  const signatureCheck = await verifyFileSignature(file);
  if (!signatureCheck.isValid) {
    logWarn(`❌ File rejected: File signature validation failed`, {
      fileName,
      detectedType: signatureCheck.detectedType,
      reason: signatureCheck.reason,
      validationId,
    });
    return {
      valid: false,
      error:
        signatureCheck.reason ||
        "File signature does not match extension. This file may be corrupted or dangerous.",
    };
  }
  logSuccess(`✅ File signature validation passed`, {
    detectedType: signatureCheck.detectedType,
    validationId,
  });

  // Step 6: Additional safety check - reject known dangerous patterns
  const dangerousExtensions = [
    ".exe",
    ".bat",
    ".cmd",
    ".sh",
    ".ps1",
    ".vbs",
    ".js",
    ".jar",
    ".app",
    ".dmg",
    ".deb",
    ".rpm",
  ];
  for (const dangerousExt of dangerousExtensions) {
    if (fileName.includes(dangerousExt)) {
      logWarn(`❌ File rejected: Contains dangerous extension pattern`, {
        fileName,
        pattern: dangerousExt,
        validationId,
      });
      return {
        valid: false,
        error: "File name contains dangerous patterns. Executable files are not allowed.",
      };
    }
  }

  logSuccess(`✅ All validation checks passed for ${file.name}`, { validationId });
  return { valid: true };
}

/**
 * Uploads a file to Firebase Storage with comprehensive security validation
 */
export async function uploadSessionMaterial(
  file: File,
  userId: string,
  sessionId?: string
): Promise<UploadedFile> {
  try {
    // ✅ SECURITY: Validate file with comprehensive checks (MIME, signature, etc.)
    const validation = await validateFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Generate secure filename: timestamp_userId_sanitizedName.extension
    const timestamp = Date.now();
    const fileExtension = file.name.split(".").pop();
    const sanitizedName = file.name.replace(/\.[^/.]+$/, "").replace(/[^a-zA-Z0-9-]/g, "_"); // Remove extension, sanitize
    const fileName = `${timestamp}_${userId}_${sanitizedName}.${fileExtension}`;

    // Create storage path
    const storagePath = sessionId
      ? `session-materials/${userId}/${sessionId}/${fileName}`
      : `session-materials/${userId}/temp/${fileName}`;

    logDebug("Uploading file", { fileName, storagePath, size: file.size });

    // Upload file
    const fileRef = ref(storage, storagePath);
    const snapshot = await uploadBytes(fileRef, file);

    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    logDebug("File uploaded successfully", { downloadURL });

    return {
      name: file.name,
      url: downloadURL,
      type: file.type,
      size: file.size,
      path: storagePath,
      uploadedAt: new Date(),
    };
  } catch (error) {
    logError("Error uploading file", error);
    throw error;
  }
}

/**
 * Uploads multiple files
 */
export async function uploadMultipleFiles(
  files: File[],
  userId: string,
  sessionId?: string
): Promise<UploadedFile[]> {
  if (files.length > MAX_FILES) {
    throw new Error(`Maximum ${MAX_FILES} files allowed`);
  }

  const uploadPromises = files.map((file) => uploadSessionMaterial(file, userId, sessionId));

  return Promise.all(uploadPromises);
}

/**
 * Deletes a file from Firebase Storage
 */
export async function deleteSessionMaterial(filePath: string): Promise<void> {
  try {
    const fileRef = ref(storage, filePath);
    await deleteObject(fileRef);
    logDebug("File deleted successfully", { filePath });
  } catch (error) {
    logError("Error deleting file", error);
    throw error;
  }
}

/**
 * Formats file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}

/**
 * Gets file icon based on file type
 */
export function getFileIcon(fileType: string): string {
  if (fileType.includes("pdf")) return "📄";
  if (fileType.includes("word") || fileType.includes("document")) return "📝";
  if (fileType.includes("powerpoint") || fileType.includes("presentation")) return "📊";
  if (fileType.includes("text/plain") || fileType.includes("txt")) return "📝";
  if (fileType.includes("image")) return "🖼️";
  return "📎";
}
